import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import ThemeSelector from '../components/ThemeSelector';
import { User, Mail, Phone, MapPin, Calendar, Download, Bell, Lock, Shield, Eye, Globe, Trash2, Upload, Camera, CreditCard, DollarSign, CheckCircle, Pause, Play, Loader2, Monitor, Key, Copy, RefreshCw, Clock, AlertTriangle, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { calculateCoolingOff, getProgressBarColor } from '../utils/coolingOff';

// Parent Data Controls Component
const ParentDataControls = () => {
  const { user } = useAuth();
  const [teens, setTeens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportStatus, setExportStatus] = useState({});
  const [deleteStatus, setDeleteStatus] = useState({});

  useEffect(() => {
    apiClient.request('/api/family/parent/dashboard')
      .then(data => setTeens(data.teens || []))
      .catch(() => setTeens([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || teens.length === 0) return null;

  const handleExport = async (teenId) => {
    setExportStatus(prev => ({ ...prev, [teenId]: 'loading' }));
    try {
      const data = await apiClient.request(`/api/compliance/parent/teen-data/${teenId}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teen_data_${teenId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExportStatus(prev => ({ ...prev, [teenId]: 'done' }));
    } catch {
      setExportStatus(prev => ({ ...prev, [teenId]: 'error' }));
    }
  };

  const handleDelete = async (teenId) => {
    if (!window.confirm('Are you sure? This will schedule deletion of your teen\'s data within 30 days.')) return;
    setDeleteStatus(prev => ({ ...prev, [teenId]: 'loading' }));
    try {
      await apiClient.request(`/api/compliance/parent/delete-teen/${teenId}`, { method: 'POST' });
      setDeleteStatus(prev => ({ ...prev, [teenId]: 'scheduled' }));
    } catch {
      setDeleteStatus(prev => ({ ...prev, [teenId]: 'error' }));
    }
  };

  return (
    <div className="p-6 bg-blue-50 rounded-xl border border-blue-200 shadow-sm" data-testid="parent-data-controls">
      <h2 className="text-xl font-semibold text-blue-800 mb-2 flex items-center gap-2">
        <Shield size={24} className="text-blue-500" />
        Parent Data Controls
      </h2>
      <p className="text-sm text-blue-600 mb-4">Review or request deletion of your teen&apos;s data. Conversation content is never included — only activity, moods, and badges.</p>
      {teens.map(teen => (
        <div key={teen.teen_id} className="flex items-center justify-between p-3 bg-white rounded-lg mb-2">
          <span className="font-medium text-gray-900">{teen.name}</span>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport(teen.teen_id)}
              disabled={exportStatus[teen.teen_id] === 'loading'}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-200 transition-colors"
              data-testid={`export-teen-${teen.teen_id}`}
            >
              {exportStatus[teen.teen_id] === 'loading' ? 'Exporting...' : exportStatus[teen.teen_id] === 'done' ? 'Downloaded' : 'Export Data'}
            </button>
            <button
              onClick={() => handleDelete(teen.teen_id)}
              disabled={deleteStatus[teen.teen_id] === 'loading' || deleteStatus[teen.teen_id] === 'scheduled'}
              className="px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-colors"
              data-testid={`delete-teen-${teen.teen_id}`}
            >
              {deleteStatus[teen.teen_id] === 'scheduled' ? 'Deletion Scheduled' : 'Request Deletion'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Consent Management Component
const ConsentManagement = () => {
  const [consent, setConsent] = useState({ marketing: false, dataProcessing: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.request('/api/users/me')
      .then(data => {
        const flags = data.consent_flags || {};
        setConsent({
          marketing: flags.marketing_consent || false,
          dataProcessing: flags.data_processing_consent !== false,
        });
      })
      .catch(() => {});
  }, []);

  const handleToggle = async (key, value) => {
    setSaving(true);
    const update = key === 'marketing' ? { marketing_consent: value } : { data_processing_consent: value };
    try {
      await apiClient.request('/api/compliance/consent', { method: 'POST', body: JSON.stringify(update) });
      setConsent(prev => ({ ...prev, [key]: value }));
    } catch {
      // revert
    }
    setSaving(false);
  };

  return (
    <div className="p-6 bg-purple-50 rounded-xl border border-purple-200 shadow-sm" data-testid="consent-management">
      <h2 className="text-xl font-semibold text-purple-800 mb-2 flex items-center gap-2">
        <Lock size={24} className="text-purple-500" />
        Data & Privacy Consent
      </h2>
      <p className="text-sm text-purple-600 mb-4">Control how your data is used. Changes take effect immediately.</p>
      <div className="space-y-3">
        <label className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer">
          <div>
            <p className="font-medium text-gray-900">Marketing communications</p>
            <p className="text-xs text-gray-500">Receive updates about new features and tips</p>
          </div>
          <input
            type="checkbox"
            checked={consent.marketing}
            onChange={(e) => handleToggle('marketing', e.target.checked)}
            disabled={saving}
            className="w-5 h-5 text-purple-600 rounded"
          />
        </label>
        <label className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer">
          <div>
            <p className="font-medium text-gray-900">Data processing for personalisation</p>
            <p className="text-xs text-gray-500">Allow your Practice Partner to reference previous sessions</p>
          </div>
          <input
            type="checkbox"
            checked={consent.dataProcessing}
            onChange={(e) => handleToggle('dataProcessing', e.target.checked)}
            disabled={saving}
            className="w-5 h-5 text-purple-600 rounded"
          />
        </label>
      </div>
      <a href="/privacy" className="inline-block mt-3 text-sm text-purple-600 hover:text-purple-800 underline">
        Read our full Privacy Policy
      </a>
    </div>
  );
};

// SMS Nudge Settings Component
const SmsNudgeSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.request('/api/sms-nudges/settings')
      .then(setSettings)
      .catch(() => setSettings({ enabled: false, preferred_day: 'sunday', preferred_time: '18:00', quiet_start: 21, quiet_end: 8 }));
  }, []);

  const update = async (key, value) => {
    setSaving(true);
    try {
      await apiClient.request('/api/sms-nudges/settings', { method: 'POST', body: JSON.stringify({ [key]: value }) });
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (e) {}
    setSaving(false);
  };

  if (!settings) return null;

  return (
    <div className="p-6 bg-green-50 rounded-xl border border-green-200 shadow-sm" data-testid="sms-nudge-settings">
      <h2 className="text-xl font-semibold text-green-800 mb-2 flex items-center gap-2">
        <Bell size={24} className="text-green-500" />
        Ava SMS Nudges
      </h2>
      <p className="text-sm text-green-600 mb-4">Get one gentle weekly text from your AvA about your goals. We&apos;ll never send more than one per week.</p>
      <div className="space-y-3">
        <label className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer">
          <div>
            <p className="font-medium text-gray-900">Enable weekly SMS nudge</p>
            <p className="text-xs text-gray-500">A short, warm message about your progress</p>
          </div>
          <input type="checkbox" checked={settings.enabled} onChange={e => update('enabled', e.target.checked)}
            disabled={saving} className="w-5 h-5 text-green-600 rounded" />
        </label>
        {settings.enabled && (
          <>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-600 mb-1 block">Preferred Day</label>
                <select value={settings.preferred_day} onChange={e => update('preferred_day', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white capitalize">
                  {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-600 mb-1 block">No SMS after</label>
                <select value={settings.quiet_start} onChange={e => update('quiet_start', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                  {[19, 20, 21, 22, 23].map(h => (
                    <option key={h} value={h}>{h}:00</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-400">Reply STOP to any SMS to unsubscribe instantly.</p>
          </>
        )}
      </div>
    </div>
  );
};

// Pause Subscription Component
const PAUSE_REASONS = [
  { id: 'taking_a_break', label: 'Taking a break' },
  { id: 'too_busy', label: 'Too busy right now' },
  { id: 'focus_on_school_work', label: 'Need to focus on school / work' },
  { id: 'financial_reasons', label: 'Financial reasons' },
  { id: 'not_finding_it_helpful', label: 'Not finding it helpful enough' },
  { id: 'prefer_real_conversations', label: 'Prefer real conversations now' },
  { id: 'privacy_concerns', label: 'Privacy concerns' },
  { id: 'other', label: 'Other' },
];

const PauseSubscriptionSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showReasons, setShowReasons] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [billingSub, setBillingSub] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    apiClient.request('/api/users/subscription/status')
      .then(data => setStatus(data))
      .catch(() => setStatus({ subscription_status: 'active' }))
      .finally(() => setLoading(false));
    apiClient.request('/api/billing/subscription')
      .then(setBillingSub)
      .catch(() => setBillingSub(null));
  }, []);

  const handleCancelSub = async () => {
    setActing(true);
    try {
      const d = await apiClient.request('/api/billing/subscription/cancel', { method: 'POST' });
      setBillingSub(s => ({ ...(s || {}), status: 'cancelling', cancel_effective_at: d.access_until }));
      setShowCancelConfirm(false);
    } catch (e) { alert(e?.message || 'Could not schedule the cancellation. Please try again.'); }
    setActing(false);
  };

  const handleUndoCancel = async () => {
    setActing(true);
    try {
      await apiClient.request('/api/billing/subscription/cancel/undo', { method: 'POST' });
      setBillingSub(s => ({ ...(s || {}), status: 'active', cancel_effective_at: null }));
    } catch (e) { alert(e?.message || 'Could not undo the cancellation.'); }
    setActing(false);
  };

  const toggleReason = (id) => {
    setSelectedReasons(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const handlePause = async () => {
    if (selectedReasons.length === 0) return;
    setActing(true);
    try {
      const data = await apiClient.request('/api/users/subscription/pause', {
        method: 'POST',
        body: JSON.stringify({ reasons: selectedReasons }),
      });
      setStatus({ subscription_status: 'paused', paused_at: data.paused_at, pause_reasons: selectedReasons });
      setShowReasons(false);
    } catch (e) { console.error(e); }
    setActing(false);
  };

  const handleUnpause = async () => {
    setActing(true);
    try {
      const data = await apiClient.request('/api/users/subscription/unpause', { method: 'POST' });
      setStatus({ subscription_status: 'active', unpaused_at: data.unpaused_at });
      setSelectedReasons([]);
    } catch (e) { console.error(e); }
    setActing(false);
  };

  if (loading) return null;

  const isPaused = status?.subscription_status === 'paused';  const pausedAt = status?.paused_at;
  const daysPaused = pausedAt ? Math.floor((Date.now() - new Date(pausedAt).getTime()) / 86400000) : 0;

  return (
    <div className={`p-6 rounded-xl border shadow-sm ${isPaused ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`} data-testid="pause-subscription-section">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Pause size={24} className={isPaused ? 'text-amber-500' : 'text-gray-400'} />
        Subscription
      </h2>

      {isPaused ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 border border-amber-300">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-sm font-medium text-amber-800">Paused{daysPaused > 0 ? ` for ${daysPaused} day${daysPaused !== 1 ? 's' : ''}` : ''}</span>
          </div>

          {status?.pause_reasons?.length > 0 && (
            <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[10px] font-medium text-amber-700 mb-1">REASONS</p>
              <div className="flex flex-wrap gap-1.5">
                {status.pause_reasons.map(r => (
                  <span key={r} className="px-2 py-0.5 text-[11px] rounded-full bg-amber-100 text-amber-800 font-medium">
                    {PAUSE_REASONS.find(pr => pr.id === r)?.label || r}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {[
              'Your data, AvA, and conversations are safely stored',
              'New features and updates are being added regularly',
              'Resume anytime — pick up right where you left off',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-green-600" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <button onClick={handleUnpause} disabled={acting}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg disabled:opacity-60"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="unpause-settings-btn">
            {acting ? <><Loader2 size={18} className="animate-spin" /> Resuming...</> : <><Play size={18} /> Resume My Subscription</>}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Need a break?</p>
            <p className="text-xs text-gray-500">
              Pause for as long as you like — 90 days, a year, forever. There is no time limit and no deadline: your data, AvA, and all your progress are preserved until you&apos;re ready to come back.
            </p>
          </div>

          {!showReasons ? (
            <button onClick={() => setShowReasons(true)}
              className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all border-2 hover:shadow-md"
              style={{ borderColor: '#F59E0B', color: '#92400E', backgroundColor: '#FFFBEB' }}
              data-testid="pause-subscription-btn">
              <Pause size={18} /> Pause My Subscription — No Time Limit
            </button>
          ) : (
            <div className="space-y-3" data-testid="pause-reasons-panel">
              <p className="text-sm font-medium text-gray-800">Help us understand — why are you pausing?</p>
              <p className="text-xs text-gray-500">Select one or more reasons. This helps us improve HiBFF for everyone.</p>

              <div className="space-y-2">
                {PAUSE_REASONS.map(reason => (
                  <label key={reason.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedReasons.includes(reason.id) ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`pause-reason-${reason.id}`}>
                    <input
                      type="checkbox"
                      checked={selectedReasons.includes(reason.id)}
                      onChange={() => toggleReason(reason.id)}
                      className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">{reason.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={handlePause}
                  disabled={acting || selectedReasons.length === 0}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                  style={{ backgroundColor: selectedReasons.length > 0 ? '#F59E0B' : '#E5E7EB', color: selectedReasons.length > 0 ? '#FFF' : '#9CA3AF' }}
                  data-testid="confirm-pause-btn">
                  {acting ? <><Loader2 size={16} className="animate-spin" /> Pausing...</> : <><Pause size={16} /> Confirm Pause</>}
                </button>
                <button onClick={() => { setShowReasons(false); setSelectedReasons([]); }}
                  className="px-5 py-3 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
                  data-testid="cancel-pause-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showReasons && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>What happens when you pause:</strong> No charges while paused, for as long as you stay paused — indefinitely if you like. Your AvA and history stay intact. The friendly check-ins at 30, 60, and 90 days are just hellos, never a deadline.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Cancel subscription — only for a live paid plan */}
      {billingSub && billingSub.plan_id && billingSub.plan_id !== 'free' && ['active', 'cancelling'].includes(billingSub.status) && (
        <div className="mt-5 pt-5 border-t border-gray-100" data-testid="cancel-subscription-section">
          {billingSub.status === 'cancelling' ? (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 space-y-3">
              <p className="text-sm font-semibold text-red-800">Cancellation scheduled</p>
              <p className="text-xs text-red-700">
                Your card will not be charged again. You keep full access
                {billingSub.cancel_effective_at ? ` until ${new Date(billingSub.cancel_effective_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ' until the end of the paid period'}, then the plan lapses automatically.
              </p>
              <button onClick={handleUndoCancel} disabled={acting}
                className="w-full py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#16A34A' }}
                data-testid="undo-cancel-subscription-btn">
                {acting ? 'Working…' : 'Undo — keep my subscription'}
              </button>
            </div>
          ) : !showCancelConfirm ? (
            <button onClick={() => setShowCancelConfirm(true)}
              className="w-full py-3 rounded-xl font-semibold text-sm border-2 transition-all hover:shadow-md"
              style={{ borderColor: '#FCA5A5', color: '#B91C1C', backgroundColor: '#FEF2F2' }}
              data-testid="cancel-subscription-btn">
              Cancel Subscription
            </button>
          ) : (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 space-y-3" data-testid="cancel-confirm-panel">
              <p className="text-sm font-semibold text-red-800">Cancel your {billingSub.plan_name || 'plan'}?</p>
              <p className="text-xs text-red-700">
                No further charges. You (and any teens on the plan) keep full access until the end of the current paid period, then the account moves to the free First Steps tier. All data, AvAs and progress are preserved.
              </p>
              <div className="flex gap-2">
                <button onClick={handleCancelSub} disabled={acting}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                  style={{ backgroundColor: '#DC2626' }}
                  data-testid="confirm-cancel-subscription-btn">
                  {acting ? 'Cancelling…' : 'Yes, cancel at period end'}
                </button>
                <button onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
                  data-testid="keep-subscription-btn">
                  Keep my plan
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── TRUSTED DEVICES SECTION ─────────────────────────────────────────────────
// Delete Account — 90-day safety pause lifecycle (never an instant wipe)
const DeleteAccountSection = () => {
  const [mode, setMode] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [typed, setTyped] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    apiClient.request('/api/account/mode').then(setMode).catch(() => setMode({}));
  }, []);

  const requestDeletion = async () => {
    setActing(true);
    try {
      await apiClient.request('/api/account/delete-request', { method: 'POST' });
      setMode(m => ({ ...(m || {}), account_mode: 'paused_90', deletion_requested: true, deletion_requested_at: new Date().toISOString() }));
      setShowConfirm(false);
      setTyped('');
    } catch (e) { alert(e?.message || 'Could not process the request. Please try again.'); }
    setActing(false);
  };

  const reactivate = async () => {
    setActing(true);
    try {
      await apiClient.request('/api/account/reactivate', { method: 'POST' });
      setMode(m => ({ ...(m || {}), account_mode: 'active', deletion_requested: false }));
    } catch (e) { alert(e?.message || 'Could not reactivate.'); }
    setActing(false);
  };

  if (!mode) return null;
  const pending = mode.deletion_requested;
  const daysLeft = pending && mode.deletion_requested_at
    ? Math.max(0, 90 - Math.floor((Date.now() - new Date(mode.deletion_requested_at).getTime()) / 86400000))
    : null;

  return (
    <div className="p-6 rounded-xl border-2 border-red-200 bg-red-50/60 shadow-sm" data-testid="delete-account-section">
      <h2 className="text-xl font-semibold text-red-800 mb-3 flex items-center gap-2">
        <Trash2 size={22} className="text-red-600" /> Delete Account
      </h2>

      {pending ? (
        <div className="space-y-4" data-testid="deletion-scheduled-panel">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-100 border border-red-300">
            <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Deletion requested — account paused</p>
              <p className="text-xs text-red-700 mt-1">
                Your subscription is cancelled and the whole family account is read-only — tutors are switched off.
                {daysLeft !== null && <> Around <strong>{daysLeft} days</strong> remain in the 90-day window.</>}{' '}
                We&apos;ll email and text you before day 90 so you can reinstate, keep your memories on indefinite
                pause, or confirm permanent deletion. Nothing is destroyed unless you confirm.
              </p>
            </div>
          </div>
          <button onClick={reactivate} disabled={acting}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg disabled:opacity-60"
            style={{ backgroundColor: '#16A34A' }}
            data-testid="reactivate-account-btn">
            {acting ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />} Reinstate My Account
          </button>
        </div>
      ) : !showConfirm ? (
        <div className="space-y-3">
          <p className="text-xs text-red-700">
            Deleting starts a <strong>90-day safety pause</strong> — your subscription is cancelled immediately,
            the account (including any linked teens) becomes read-only, and tutors are switched off. Before day 90
            we check in by email and SMS: reinstate everything, keep your memories on indefinite pause, or confirm
            total deletion of everything.
          </p>
          <button onClick={() => setShowConfirm(true)}
            className="w-full py-3 rounded-xl font-semibold text-sm border-2 border-red-300 text-red-700 bg-white hover:bg-red-50 transition-colors"
            data-testid="delete-account-btn">
            Delete Account…
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="delete-confirm-panel">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-100 border border-red-300">
            <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-800">
              <strong>You will lose access to everything</strong> — conversations, tutors, trophies and progress for
              you and your linked teens. Billing stops now and the account goes read-only for 90 days. Type{' '}
              <strong>DELETE</strong> to continue.
            </p>
          </div>
          <input value={typed} onChange={e => setTyped(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="w-full px-4 py-2.5 border-2 border-red-200 rounded-xl focus:border-red-500 outline-none text-sm"
            data-testid="delete-confirm-input" />
          <div className="flex gap-2">
            <button onClick={requestDeletion} disabled={acting || typed.trim().toUpperCase() !== 'DELETE'}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="confirm-delete-account-btn">
              {acting ? 'Processing…' : 'Delete my account'}
            </button>
            <button onClick={() => { setShowConfirm(false); setTyped(''); }}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
              data-testid="abort-delete-account-btn">
              Keep my account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TrustedDevicesSection = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    apiClient.request('/api/users/me/trusted-devices')
      .then(data => setDevices(data.devices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const revokeDevice = async (deviceId) => {
    setRevoking(deviceId);
    try {
      await apiClient.request(`/api/users/me/trusted-devices/${deviceId}`, { method: 'DELETE' });
      setDevices(prev => prev.filter(d => d.device_id !== deviceId));
    } catch {}
    setRevoking(null);
  };

  const revokeAll = async () => {
    if (!window.confirm('Remove all trusted devices? You\'ll need 2FA on every login.')) return;
    try {
      await apiClient.request('/api/users/me/trusted-devices', { method: 'DELETE' });
      setDevices([]);
    } catch {}
  };

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="trusted-devices-section">
      <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Monitor size={24} className="text-blue-500" />
        Trusted Devices
      </h2>
      <p className="text-sm text-gray-500 mb-4">Devices that skip two-factor authentication for 30 days.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : devices.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400">No trusted devices. When you verify via 2FA, check &quot;Trust this device&quot; to add one.</div>
      ) : (
        <div className="space-y-3">
          {devices.map(d => (
            <div key={d.device_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200" data-testid={`device-${d.device_id}`}>
              <div className="flex items-center gap-3">
                <Monitor size={20} className="text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{d.device_name}</p>
                  <p className="text-xs text-gray-400">
                    Added {new Date(d.created_at).toLocaleDateString()} · {d.days_remaining} days remaining
                  </p>
                </div>
              </div>
              <button
                onClick={() => revokeDevice(d.device_id)}
                disabled={revoking === d.device_id}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                data-testid={`revoke-device-${d.device_id}`}
              >
                {revoking === d.device_id ? 'Revoking...' : 'Revoke'}
              </button>
            </div>
          ))}
          {devices.length > 1 && (
            <button onClick={revokeAll} className="text-xs text-red-600 hover:text-red-700 font-medium mt-2" data-testid="revoke-all-devices-btn">
              Revoke all devices
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── RECOVERY CODES SECTION ──────────────────────────────────────────────────
const RecoveryCodesSection = ({ twoFaEnabled }) => {
  const [status, setStatus] = useState({ total: 0, remaining: 0, generated_at: null });
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.request('/api/users/me/recovery-codes/status')
      .then(data => setStatus(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRegenerate = async () => {
    if (codes && !window.confirm('This will invalidate all existing recovery codes. Continue?')) return;
    setRegenerating(true);
    try {
      const data = await apiClient.request('/api/users/me/recovery-codes/regenerate', { method: 'POST' });
      setCodes(data.recovery_codes);
      setStatus(prev => ({ ...prev, total: 8, remaining: 8, generated_at: new Date().toISOString() }));
    } catch {}
    setRegenerating(false);
  };

  const copyToClipboard = () => {
    if (!codes) return;
    navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!twoFaEnabled) return null;

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="recovery-codes-section">
      <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Key size={24} className="text-amber-500" />
        Recovery Codes
      </h2>
      <p className="text-sm text-gray-500 mb-4">One-time backup codes for when you can&apos;t receive SMS.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : (
        <>
          {status.remaining > 0 && !codes && (
            <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${status.remaining <= 2 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              {status.remaining <= 2 ? <AlertTriangle size={18} className="text-red-500" /> : <CheckCircle size={18} className="text-green-600" />}
              <div>
                <p className="text-sm font-medium text-gray-800">{status.remaining} of {status.total} codes remaining</p>
                {status.remaining <= 2 && <p className="text-xs text-red-600">You&apos;re running low — regenerate new codes soon.</p>}
              </div>
            </div>
          )}

          {codes && (
            <div className="mb-4">
              <div className="bg-gray-900 rounded-xl p-5 mb-3">
                <div className="grid grid-cols-2 gap-2">
                  {codes.map((code, i) => (
                    <div key={i} className="font-mono text-sm text-green-400 bg-gray-800 rounded px-3 py-2 text-center" data-testid={`recovery-code-${i}`}>
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={copyToClipboard} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="copy-codes-btn">
                  {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy all</>}
                </button>
                <span className="text-xs text-gray-400 ml-2">Save these somewhere safe. Each code works only once.</span>
              </div>
            </div>
          )}

          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="regenerate-codes-btn"
          >
            {regenerating ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><RefreshCw size={14} /> {codes ? 'Regenerate New Codes' : status.remaining > 0 ? 'View / Regenerate Codes' : 'Generate Recovery Codes'}</>}
          </button>
        </>
      )}
    </div>
  );
};

// ─── LOGIN ACTIVITY SECTION ──────────────────────────────────────────────────
const LoginActivitySection = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request('/api/users/me/login-activity')
      .then(data => setActivities(data.activities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getStatusColor = (status) => status === 'failed' ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';
  const getActionLabel = (action) => {
    switch (action) {
      case 'user_login': return 'Login';
      case 'login_failed': return 'Failed Login';
      case '2fa_verified': return '2FA Verified';
      case 'user_registered': return 'Registered';
      default: return action;
    }
  };

  const [activityOpen, setActivityOpen] = useState(false);

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="login-activity-section">
      <button
        onClick={() => setActivityOpen(o => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid="login-activity-toggle"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Clock size={24} className="text-indigo-500" />
            Login Activity
          </h2>
          <p className="text-sm text-gray-500">Recent sign-in activity on your account.</p>
        </div>
        <ChevronDown size={20} className={`text-gray-400 transition-transform ${activityOpen ? 'rotate-180' : ''}`} />
      </button>

      {activityOpen && (
      <div className="mt-4">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : activities.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400">No activity recorded yet.</div>
      ) : (
        <div className="space-y-2">
          {activities.map((a, i) => (
            <div key={i} className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0" data-testid={`activity-${i}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.status === 'failed' ? 'bg-red-100' : 'bg-green-100'}`}>
                  {a.status === 'failed' ? <X size={14} className="text-red-600" /> : <CheckCircle size={14} className="text-green-600" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{getActionLabel(a.action)}</p>
                  <p className="text-xs text-gray-400">{a.device || 'Unknown'} {a.method !== 'password' ? `· ${a.method}` : ''}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">{new Date(a.timestamp).toLocaleDateString()}</p>
                <p className="text-xs text-gray-400">{new Date(a.timestamp).toLocaleTimeString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isTeen = user?.age_band === 'teen';
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_picture || null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  // Ref-driven file picker — using a programmatic `.click()` here instead of
  // <label htmlFor=...> because mobile browsers (Chrome on Android, Safari
  // on iOS) silently refuse to forward label clicks to inputs hidden via
  // `display:none`, which was the root cause of the "Choose File" button
  // doing nothing on the production teen dashboard.
  const photoFileInputRef = useRef(null);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [profilePhone, setProfilePhone] = useState('');
  const [phoneStatus, setPhoneStatus] = useState({ has_phone: false, masked_phone: null, two_fa_enabled: false });
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    avaCallReminders: true,
    groupInvites: true,
    weeklyReports: false,
    marketingEmails: false,
    twoFactorAuth: false,
    publicProfile: false,
    shareProgress: true,
    dataSharing: false
  });

  // Load phone status on mount
  useEffect(() => {
    apiClient.request('/api/users/me/phone-status')
      .then(data => {
        setPhoneStatus(data);
        if (data.has_phone && data.masked_phone) {
          setProfilePhone(data.masked_phone);
        }
      })
      .catch(() => {});
  }, []);

  // Keep the displayed portrait in sync with the auth context (e.g. after
  // a refreshUser() following upload/delete, or after first-load hydration).
  useEffect(() => {
    setProfilePhoto(user?.profile_picture || null);
  }, [user?.profile_picture]);

  // Calculate cooling-off period
  const subscription = user?.subscriptionDetails || {};
  const coolingOff = subscription.subscribedDate ? calculateCoolingOff(subscription.subscribedDate) : null;

  const handlePhotoUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file after a reject
    if (!file) return;
    await uploadPhotoFile(file);
  };

  // Camera capture — teens take a real photo with the device camera
  const [showCamera, setShowCamera] = useState(false);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const openCamera = async () => {
    setPhotoError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      cameraStreamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => { if (cameraVideoRef.current) cameraVideoRef.current.srcObject = stream; }, 100);
    } catch {
      setPhotoError('Camera unavailable — check permissions, or upload a photo instead.');
    }
  };

  const closeCamera = () => {
    if (cameraStreamRef.current) { cameraStreamRef.current.getTracks().forEach(t => t.stop()); cameraStreamRef.current = null; }
    setShowCamera(false);
  };

  const captureFromCamera = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      closeCamera();
      await uploadPhotoFile(new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  const uploadPhotoFile = async (file) => {
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const token =
        localStorage.getItem('hibff_auth_token') ||
        sessionStorage.getItem('hibff_auth_token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${baseUrl}/api/users/me/portrait`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || 'Upload failed. Please try a different image.');
      }
      setProfilePhoto(data.profile_picture);
      setShowPhotoModal(false);
      try { await refreshUser(); } catch { /* non-fatal */ }
    } catch (err) {
      setPhotoError(err?.message || 'Upload failed. Please try a different image.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoRemove = async () => {
    if (!profilePhoto) return;
    if (!window.confirm('Remove your profile photo?')) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const token =
        localStorage.getItem('hibff_auth_token') ||
        sessionStorage.getItem('hibff_auth_token');
      const resp = await fetch(`${baseUrl}/api/users/me/portrait`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || 'Could not remove photo.');
      }
      setProfilePhoto(null);
      try { await refreshUser(); } catch { /* non-fatal */ }
    } catch (err) {
      setPhotoError(err?.message || 'Could not remove photo.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleDataExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Real GDPR Article 20 export — calls /api/compliance/export which
      // returns a complete JSON of profile + sessions + challenges + mood
      // entries + badges + charters + notifications.
      const data = await apiClient.request('/api/compliance/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hibff-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('✅ Your data has been exported successfully!');
    } catch (e) {
      alert('Could not export your data. Please email privacy@hibff.com and we will run the export for you.');
      console.error('Data export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  const handleCloseAccount = async () => {
    if (closing) return;
    const confirmed = window.confirm(
      'Are you sure you want to close your HiBFF account?\n\n' +
      "Your data will be preserved for 90 days in case you change your mind.\n" +
      'After 90 days, it will be permanently deleted in line with GDPR Article 17 (Right to Erasure).\n\n' +
      'This cannot be undone after the 90-day window.'
    );
    if (!confirmed) return;
    setClosing(true);
    try {
      await apiClient.request('/api/compliance/delete', { method: 'POST' });
      alert(
        '✅ Your account has been scheduled for deletion.\n\n' +
        'You will be signed out now. If you change your mind in the next 90 days, ' +
        'just sign back in to reactivate.'
      );
      // Sign the user out
      localStorage.removeItem('hibff_auth_token');
      sessionStorage.removeItem('hibff_auth_token');
      localStorage.removeItem('hibff_user_cache');
      window.location.href = '/';
    } catch (e) {
      alert('Could not schedule deletion. Please email privacy@hibff.com.');
      console.error('Account closure failed:', e);
      setClosing(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600 mb-8">Manage your account preferences and settings</p>

        <div className="space-y-6">
          {/* Profile Settings */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <User size={24} className="text-blue-500" />
              Profile Settings
            </h2>

            {/* Profile Photo */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Profile Photo</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto.startsWith('http') ? profilePhoto : `${process.env.REACT_APP_BACKEND_URL}${profilePhoto}`}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover"
                      data-testid="settings-profile-photo"
                    />
                  ) : (
                    <div
                      className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center text-white text-3xl font-bold"
                      data-testid="settings-profile-photo-placeholder"
                    >
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <button
                    onClick={() => { setPhotoError(''); setShowPhotoModal(true); }}
                    className="absolute bottom-0 right-0 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg"
                    data-testid="settings-profile-photo-edit-btn"
                  >
                    <Camera size={16} />
                  </button>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    {isTeen
                      ? 'Use your real photo — take it live with your camera so everyone knows it\u2019s really you.'
                      : 'JPG, PNG, or WEBP up to 8 MB. Photos are re-encoded server-side to strip metadata.'}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={openCamera}
                      disabled={photoUploading}
                      className="text-sm px-3 py-1.5 rounded-lg text-white font-medium disabled:opacity-50 flex items-center gap-1.5"
                      style={{ backgroundColor: '#F5831F' }}
                      data-testid="settings-take-photo-btn"
                    >
                      <Camera size={14} /> Take Photo
                    </button>
                    <button
                      onClick={() => { setPhotoError(''); setShowPhotoModal(true); }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      data-testid="settings-change-photo-btn"
                    >
                      Change Photo
                    </button>
                    {profilePhoto && (
                      <button
                        onClick={handlePhotoRemove}
                        disabled={photoUploading}
                        className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                        data-testid="settings-remove-photo-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {photoError && (
                    <p className="text-xs text-red-600 mt-2" data-testid="settings-photo-error">{photoError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Camera capture modal */}
            {showCamera && (
              <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" data-testid="camera-capture-modal">
                <div className="bg-white rounded-2xl p-5 max-w-md w-full text-center">
                  <p className="font-semibold text-gray-900 mb-3">Smile! Take your profile photo</p>
                  <video ref={cameraVideoRef} autoPlay playsInline muted className="w-full rounded-xl mb-4 bg-black" style={{ maxHeight: 320, objectFit: 'cover', transform: 'scaleX(-1)' }} />
                  <div className="flex gap-3 justify-center">
                    <button onClick={captureFromCamera} className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: '#F5831F' }} data-testid="camera-capture-btn">
                      Capture
                    </button>
                    <button onClick={closeCamera} className="px-6 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600" data-testid="camera-cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={profileEmail}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  data-testid="settings-email-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  Phone Number
                  {phoneStatus.two_fa_enabled && (
                    <span className="text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <Shield size={10} /> 2FA Active
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => { setProfilePhone(e.target.value); setPhoneMessage(''); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 555 123 4567"
                    data-testid="settings-phone-input"
                  />
                  <button
                    onClick={async () => {
                      const cleaned = profilePhone.replace(/[\s\-()]/g, '');
                      if (!cleaned || cleaned.startsWith('***')) return;
                      if (!/^\+\d{7,15}$/.test(cleaned)) {
                        setPhoneMessage('Use international format: +14155551234');
                        return;
                      }
                      setPhoneSaving(true);
                      setPhoneMessage('');
                      try {
                        const data = await apiClient.request('/api/users/me/phone', {
                          method: 'PUT',
                          body: JSON.stringify({ phone: cleaned }),
                        });
                        setPhoneStatus({ has_phone: true, masked_phone: data.masked_phone, two_fa_enabled: true });
                        setProfilePhone(data.masked_phone);
                        setPhoneMessage('Phone saved — 2FA is now active');
                      } catch (err) {
                        setPhoneMessage(err.message || 'Failed to save phone');
                      }
                      setPhoneSaving(false);
                    }}
                    disabled={phoneSaving || !profilePhone || profilePhone.startsWith('***')}
                    className="px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-phone-btn"
                  >
                    {phoneSaving ? 'Saving...' : 'Save'}
                  </button>
                  {phoneStatus.has_phone && !isTeen && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Remove your phone number? This will disable 2FA.')) return;
                        try {
                          await apiClient.request('/api/users/me/phone', { method: 'DELETE' });
                          setPhoneStatus({ has_phone: false, masked_phone: null, two_fa_enabled: false });
                          setProfilePhone('');
                          setPhoneMessage('Phone removed — 2FA disabled');
                        } catch (err) {
                          setPhoneMessage(err.message || 'Failed to remove');
                        }
                      }}
                      className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid="remove-phone-btn"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {phoneMessage && (
                  <p className={`text-xs mt-1.5 ${phoneMessage.includes('active') || phoneMessage.includes('disabled') ? 'text-green-600' : 'text-red-500'}`} data-testid="phone-message">
                    {phoneMessage}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Include country code (e.g. +1 for US, +44 for UK). Adding a phone enables SMS two-factor authentication.
                  {isTeen && ' For your safety, your account always keeps a reachable number — to change it, save and verify a new one and the old number is replaced automatically.'}
                </p>
              </div>
              {isTeen && user?.dob && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700" data-testid="settings-dob">
                    {new Date(user.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <p className="text-xs mt-1 font-medium" style={{ color: '#F5831F' }} data-testid="settings-dob-gift-note">
                    🎁 On your birthday, HiBFF drops a surprise gift into your Vault. We never forget.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  placeholder="City, Country"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <button
              onClick={async () => {
                setProfileSaving(true);
                try {
                  await apiClient.request('/api/users/me', {
                    method: 'PATCH',
                    body: JSON.stringify({ name: profileName }),
                  });
                } catch {}
                setProfileSaving(false);
              }}
              disabled={profileSaving}
              className="mt-4 px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {profileSaving ? 'Saving...' : 'Save Profile'}
            </button>

            {/* Email 2FA Toggle */}
            {!phoneStatus.has_phone && (
              <div className="mt-6 p-4 rounded-xl bg-purple-50 border border-purple-200" data-testid="email-2fa-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-800">Email Two-Factor Authentication</p>
                      <p className="text-xs text-gray-500">Receive a 6-digit code via email on each login. Enable this if you prefer not to use SMS.</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneStatus.email_2fa_enabled || false}
                      onChange={async (e) => {
                        const enabled = e.target.checked;
                        try {
                          const data = await apiClient.request('/api/users/me/email-2fa', {
                            method: 'PUT',
                            body: JSON.stringify({ enabled }),
                          });
                          setPhoneStatus(prev => ({ ...prev, email_2fa_enabled: data.email_2fa_enabled, two_fa_enabled: data.email_2fa_enabled || prev.has_phone }));
                          setPhoneMessage(data.message);
                        } catch (err) {
                          setPhoneMessage(err.message || 'Failed to update');
                        }
                      }}
                      className="sr-only peer"
                      data-testid="email-2fa-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Appearance */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye size={24} className="text-purple-500" />
              Appearance
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-700">Dashboard Theme</p>
                <p className="text-sm text-gray-500">Choose your preferred color scheme</p>
              </div>
              <ThemeSelector />
            </div>
          </div>

          {/* Notifications */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Bell size={24} className="text-green-500" />
              Notifications
            </h2>
            <div className="space-y-4">
              {[
                { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive email updates about your account' },
                { key: 'pushNotifications', label: 'Push Notifications', desc: 'Get browser notifications for important updates' },
                { key: 'avaCallReminders', label: 'Practice Reminders', desc: 'A nudge to keep your social skills sharp' },
                { key: 'groupInvites', label: 'Group Invites', desc: 'Notifications for group session invitations' },
                { key: 'weeklyReports', label: 'Weekly Progress Reports', desc: 'How your real-world social skills are growing' },
                { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Updates about new features and promotions' }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-700">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[item.key]}
                      onChange={() => toggleSetting(item.key)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Privacy & Security */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield size={24} className="text-red-500" />
              Privacy & Security
            </h2>
            <div className="space-y-4">
              {[
                { key: 'publicProfile', label: 'Public Profile', desc: 'Allow others to find and connect with you' },
                { key: 'shareProgress', label: 'Share Progress', desc: 'Let your group cheer on your real-world wins' },
                { key: 'dataSharing', label: 'Anonymous Data Sharing', desc: 'Help improve HiBFF with anonymous usage data' }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-700">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[item.key]}
                      onChange={() => toggleSetting(item.key)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Trusted Devices */}
          <TrustedDevicesSection />

          {/* Recovery Codes */}
          <RecoveryCodesSection twoFaEnabled={phoneStatus.two_fa_enabled} />

          {/* Login Activity */}
          <LoginActivitySection />

          {/* Subscription Pause */}
          <PauseSubscriptionSection />

          {/* Delete Account — 90-day safety lifecycle */}
          <DeleteAccountSection />

          {/* Subscription & Billing */}
          {subscription.plan && (
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard size={24} className="text-blue-500" />
                💳 Subscription & Billing
              </h2>

              {/* Current Plan */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm text-gray-600">Current Plan</p>
                    <p className="text-lg font-bold text-gray-900">{subscription.plan} (${subscription.price}/month)</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                  <div>
                    <p className="text-gray-600">Next Billing Date</p>
                    <p className="font-semibold text-gray-900">{subscription.nextBillingDate}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Payment Method</p>
                    <p className="font-semibold text-gray-900">{subscription.paymentMethod}</p>
                  </div>
                </div>
              </div>

              {/* 90-Day Money-Back Guarantee */}
              {coolingOff && (
                <div className={`p-6 border-2 rounded-xl mb-6 ${coolingOff.refundEligible ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <Shield size={24} className={coolingOff.refundEligible ? 'text-green-600' : 'text-gray-600'} />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2">🛡️ 90-Day Money-Back Guarantee</h3>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between">
                          <span>Subscribed:</span>
                          <span className="font-semibold">{subscription.subscribedDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cooling-Off Ends:</span>
                          <span className="font-semibold">{coolingOff.endsDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Days Remaining:</span>
                          <span className="font-semibold">{coolingOff.daysRemaining} days</span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>{coolingOff.percentElapsed}% elapsed</span>
                          <span>{coolingOff.daysRemaining} days left</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${getProgressBarColor(coolingOff.percentElapsed)}`}
                            style={{ width: `${coolingOff.percentElapsed}%` }}
                          ></div>
                        </div>
                      </div>

                      {coolingOff.refundEligible ? (
                        <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-900">
                            💡 <strong>You can cancel anytime before {coolingOff.endsDate} for a full refund of all payments made.</strong>
                          </p>
                        </div>
                      ) : (
                        <div className="mt-4 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-700">
                            ℹ️ Your cooling-off period has expired. You can still cancel anytime without future charges, but previous payments are non-refundable.
                          </p>
                        </div>
                      )}

                      {/* Cancel Button */}
                      <button
                        onClick={() => navigate('/cancel-subscription')}
                        className={`mt-4 w-full py-3 font-semibold rounded-lg transition-colors ${
                          coolingOff.refundEligible
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                      >
                        {coolingOff.refundEligible ? 'Cancel Subscription & Get Refund' : 'Cancel Subscription'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50">
                  Update Payment Method
                </button>
                <button className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50">
                  View Billing History
                </button>
              </div>
            </div>
          )}

          {/* Data Export - GREEN BUTTON */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="settings-data-export-section">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Download size={24} className="text-green-500" />
              Data Export
            </h2>
            <p className="text-gray-600 mb-4">
              Download all your data including conversations, mood tracking, and
              achievements. Complies with GDPR Article 20 (Right to Data Portability).
            </p>
            <button
              onClick={handleDataExport}
              disabled={exporting}
              data-testid="settings-export-btn"
              className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-wait"
            >
              {exporting ? (
                <><Loader2 size={20} className="animate-spin" /> Preparing your data…</>
              ) : (
                <><Download size={20} /> Export All Data</>
              )}
            </button>
          </div>

          {/* Close My Account */}
          <div className="p-6 bg-gray-50 rounded-xl border border-gray-200 shadow-sm" data-testid="settings-close-account-section">
            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Trash2 size={24} className="text-gray-500" />
              Close My Account
            </h2>
            <p className="text-gray-600 mb-4">
              If you close your account, your data will be preserved for 90 days
              in case you change your mind. After 90 days, it is permanently
              deleted under GDPR Article 17.
            </p>
            <button
              onClick={handleCloseAccount}
              disabled={closing}
              data-testid="settings-close-account-btn"
              className="px-6 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              {closing ? 'Working…' : 'Close Account'}
            </button>
          </div>

          {/* Parent Data Controls — only shown for parent accounts */}
          <ParentDataControls />

          {/* Consent Management */}
          <ConsentManagement />

          {/* SMS Nudge Settings */}
          <SmsNudgeSettings />
        </div>
      </div>

      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => !photoUploading && setShowPhotoModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Profile Photo</h3>
            <p className="text-xs text-gray-500 mb-4">JPG, PNG or WEBP · up to 8 MB · 64×64 to 4096×4096 px · re-encoded to strip metadata.</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <Upload size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">{photoUploading ? 'Uploading…' : 'Tap below to select a photo'}</p>
              <input
                ref={photoFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoUpload}
                disabled={photoUploading}
                data-testid="settings-photo-file-input"
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              />
              <button
                type="button"
                onClick={() => photoFileInputRef.current?.click()}
                disabled={photoUploading}
                className={`cursor-pointer px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors inline-block ${photoUploading ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="settings-photo-choose-btn"
              >
                {photoUploading ? 'Uploading…' : 'Choose File'}
              </button>
            </div>
            {photoError && (
              <p className="text-sm text-red-600 mt-3" data-testid="settings-photo-modal-error">{photoError}</p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setShowPhotoModal(false)}
                disabled={photoUploading}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Settings;
